"use client";

import { JapanArea, JapanDatasourceTopAttraction, RegionListQuery } from "@/types/generated";
import { ChevronRightIcon } from "lucide-react";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import type {
  AttractionCenter,
  Attractions,
} from "../hotel-details/location-section/LocationMapView.types";

// Lazy load MapView to avoid bundling @vis.gl/react-google-maps in initial JS
const MapView = dynamic(() => import("../map-view/MapView"), {
  ssr: false,
  loading: () => (
    <div className="fixed top-0 left-0 z-[50] h-full w-full animate-pulse bg-gray-200" />
  ),
});
import {
  JapanAttractionWithAreaName,
  RestAttractionCard,
} from "./RestAttractionCard";
import { mapAreasToDisplayData } from "./utils";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import { MapBrandIcon } from "@app/[locale]/tourist-attraction/[city_slug]/[slug]/util";
import { Button } from "@/components/atoms/button";
import { RichtextRenderer } from "@/components/atoms/rich-text-render";

interface DestinationTopAttractionsProps {
  destinationAreaData: JapanArea[];
  topAttractionsDatasource: JapanDatasourceTopAttraction;
  regionName?: string;
  nameArea?: string;
  brandIcons?: MapBrandIcon[];
  seeAllAttractions?: string | null;
  title?: string | null;
  regionData?: RegionListQuery;
}

/**
 * DestinationTopAttractions Component
 * Displays top attractions and regional highlights with a toggleable map view.
 * Handles dynamic rendering, layout recalculations, and data transformation for map display.
 */
export const DestinationTopAttractions = ({
  destinationAreaData,
  topAttractionsDatasource,
  regionName,
  nameArea,
  brandIcons,
  seeAllAttractions,
  title,
  regionData,
}: DestinationTopAttractionsProps) => {
  const { top_attractions } = topAttractionsDatasource || {};
  const topAttractionIds =
    top_attractions.map(
      (attraction: JapanDatasourceTopAttraction) => attraction.documentId,
    ) || [];

  // Flatten all attractions and attach their area names for reference
  const allAttractionsWithAreaName = destinationAreaData.reduce<
    JapanAttractionWithAreaName[]
  >((acc, area) => {
    for (const attraction of area?.attractions || []) {
      if (attraction) {
        acc.push({
          ...attraction,
          areaName: area.item_name,
          areaTitle: area.area_name,
        });
      }
    }
    return acc;
  }, []);

  // Match top attraction IDs with detailed data
  const finalTopAttractions = topAttractionIds
    .map((docId: string) => {
      return allAttractionsWithAreaName.find(
        (attr) => attr.documentId === docId,
      );
    })
    .filter(Boolean) as JapanAttractionWithAreaName[];

  const remainingAttractions = finalTopAttractions.slice(3);
  const locale = useLocale();

  const [openMap, setOpenMap] = useState(false);
  const [transformedData, setTransformedData] = useState<Attractions[]>([]);
  const [mapCenter, setMapCenter] = useState<AttractionCenter | null>(null);

  // Compute attraction section title using CMS or fallback structure
  const titleDestinationAttraction = useMemo(() => {
    const countryName = nameArea || "日本";
    const titleFromCMS = title
      ? title.replace("{0}", countryName)
      : countryName + "の人気の観光地";
    return titleFromCMS;
  }, [nameArea, title]);

  const { japanAreas } = useJapanAreasStore();

  // Transform area data safely within animation frame to avoid blocking render
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (destinationAreaData && destinationAreaData.length > 0) {
        const mappedData = mapAreasToDisplayData(
          destinationAreaData,
          japanAreas,
          regionData,
        );
        setTransformedData(mappedData);
      } else {
        setTransformedData([]);
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [destinationAreaData, japanAreas, regionData]);

  const handleOpenMap = () => {
    if (!destinationAreaData || destinationAreaData.length === 0) return;
    const firstArea = destinationAreaData?.[0];

    if (regionName && firstArea) {
      setMapCenter({
        lat: Number(firstArea.latitude) || 38.1895,
        lng: Number(firstArea.longitude) || 137.1917,
        zoom: Number(firstArea.map_zoom_ratio) || 5,
      });
    } else {
      setMapCenter({
        lat: 38.1895,
        lng: 137.1917,
        zoom: 5,
      });
    }
    setOpenMap(true);
  };

  return (
    <>
      {openMap && mapCenter && (
        <MapView
          data={transformedData}
          center={mapCenter}
          className="fixed top-0 left-0 !z-[50] h-full w-full overflow-hidden"
          setOpenMap={setOpenMap}
          isDestinationPage={true}
          brandIcons={brandIcons}
        />
      )}

      <h3 className="mb-0 text-center leading-11 font-[var(--font-semibold)] tracking-[-0.02em] text-[var(--text-base-primary)] max-md:mt-5 max-md:text-2xl max-md:leading-9 md:text-[24px] xl:text-4xl">
        {titleDestinationAttraction}
      </h3>
      <div className="grid grid-cols-1 max-md:gap-5 md:grid-cols-[32%_32%_32%] md:gap-[2%]">
        {finalTopAttractions.map((attraction, index) => (
          <button
            type="button"
            onClick={() => {
              globalThis.location.href = `/${locale}/${attraction?.slug}`;
            }}
            className={`col-span-1 ${index >= 3 ? "md:hidden" : ""} cursor-pointer overflow-hidden rounded-lg bg-white shadow-lg`}
            key={attraction.documentId}
          >
            <div className="relative h-[288px] w-full max-md:h-[193px]">
              <Image
                src={
                  attraction.properties?.overview_image[0]?.cloudflare_url || ""
                }
                alt={
                  attraction.properties?.overview_image[0]?.alternativeText ||
                  ""
                }
                aria-label={
                  attraction.properties?.overview_image[0]?.caption || ""
                }
                fill
                className="object-cover"
              />
              <div className="absolute top-0 right-0 flex h-[68px] w-[68px] items-center justify-center rounded-bl-xl bg-[var(--color-secondary-400)] p-4 text-2xl font-bold text-blue-900">
                {index + 1}
              </div>
            </div>
            <div className="flex min-h-[108px] flex-col justify-start gap-2 px-6 pt-4 pb-2 text-center max-md:min-h-[76px] max-md:px-3 max-md:pt-2 max-md:pb-3">
              <div className="line-clamp-2 text-2xl leading-9 max-md:text-xl max-md:leading-8">
                <RichtextRenderer
                  customStyle={{
                    "": {
                      fontWeight: 600,
                      color: "var(--text-base-primary)",
                    },
                  }}
                  content={attraction?.properties?.name || ""}
                />
              </div>
              <div className="text-base leading-6 font-normal text-[var(--text-gray-normal)] max-md:text-sm max-md:leading-5">
                {attraction.areaTitle}
              </div>
            </div>
          </button>
        ))}
      </div>

      {finalTopAttractions?.length > 3 && (
        <div className="flex justify-center py-4">
          <Button
            onClick={handleOpenMap}
            variant={"primary"}
            hierarchy={"tonal"}
            className="h-12 text-base leading-6 font-[var(--font-medium)]"
          >
            {seeAllAttractions || "すべての観光地を見る"}
            <ChevronRightIcon className="h-5 w-5" />
          </Button>
        </div>
      )}

      {remainingAttractions?.length > 0 && (
        <div className="hidden md:grid md:grid-cols-2 md:gap-x-6 md:gap-y-6">
          <div className="max-w-[588px] space-y-6">
            {remainingAttractions
              .filter((_, indexInRemaining) => (4 + indexInRemaining) % 2 === 0)
              .map((attraction) => {
                const originalIndex = finalTopAttractions.indexOf(attraction);
                return (
                  <RestAttractionCard
                    key={attraction.documentId}
                    attraction={attraction}
                    rank={originalIndex + 1}
                  />
                );
              })}
          </div>

          <div className="max-w-[588px] space-y-6">
            {remainingAttractions
              .filter(
                (_, indexInRemaining) => (4 + indexInRemaining) % 2 !== 0,
              )
              .map((attraction) => {
                const originalIndex = finalTopAttractions.indexOf(attraction);
                return (
                  <RestAttractionCard
                    key={attraction.documentId}
                    attraction={attraction}
                    rank={originalIndex + 1}
                  />
                );
              })}
          </div>
        </div>
      )}
    </>
  );
};
